import React from "react";
import { buildCompanyLogoUrl } from "../../utils/logo";

export const EMPTY_FILTERS = {
  CountryIdentifier: "",
  StateExIDs: [],
  ParentUtilityExIDs: [],
  SearchValue: "",
  Electric: false,
  NatGas: false,
  Active: true,
  Inactive: false,
};

export const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
export const normalizeGuidValue = (value) => !value || value === EMPTY_GUID ? "" : String(value);
export const utilitySelectStyles = {
  control: (base) => ({ ...base, minHeight: "34px", borderColor: "var(--ct-input-border-color)" }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

export const textOrDash = (value) => value || <span className="text-muted">-</span>;

export const isUtilityActive = (utility) => {
  const active = utility?.active ?? utility?.Active;
  if (active === undefined || active === null) return true;
  if (typeof active === "boolean") return active;
  return String(active).toLowerCase() === "true" || String(active) === "1";
};

export const LogoPreview = ({ utility, className = "", imageStyle = {} }) => {
  const logoUrl = utility ? buildCompanyLogoUrl(utility.webSite || utility.WebSite) : "";
  return (
    <div className={`image-container-logo ${className}`.trim()}>
      {logoUrl ? (
        <img
          src={logoUrl}
          alt="Utility Logo"
          style={{ ...imageStyle, width: "auto", maxWidth: "none" }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : null}
    </div>
  );
};

export const csvValue = (value) => {
  const text = String(value ?? "").replace(/"/g, '""');
  return `"${text}"`;
};

export const downloadCsv = (rows, fileName) => {
  const blob = new Blob([rows], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export const getIdentifier = (utility) => {
  const identifier = utility?.enerexIdentifier || utility?.EnerexIdentifier || utility?.identifier || utility?.id || utility?.Id;
  if (identifier && identifier !== EMPTY_GUID) return identifier;
  return utility?.id ?? utility?.Id ?? "";
};

export const getEnerexIdentifier = (utility) =>
  normalizeGuidValue(
    utility?.enerexIdentifier ||
    utility?.EnerexIdentifier ||
    utility?.enerexUtilityId ||
    utility?.EnerexUtilityId ||
    utility?.value
  );

export const getUtilityId = (utility) => utility?.id ?? utility?.Id ?? 0;
export const getId = (item) => item?.id ?? item?.Id;
export const getActionId = (item) => item?.id ?? item?.Id ?? item?.utilityStateId ?? item?.UtilityStateId ?? item?.utilityAliasId ?? item?.UtilityAliasId ?? item?.utilityRateClassId ?? item?.UtilityRateClassId ?? item?.utilityFormatId ?? item?.UtilityFormatId;
export const getStateId = (state) => state?.stateId ?? state?.StateId ?? state?.stateID ?? state?.StateID ?? getId(state);
export const getStateOptionValue = (state) => getStateId(state);
export const getStateDisplayName = (state) => state?.displayName || state?.DisplayName || state?.name || state?.Name || state?.stateName || state?.StateName || "";
const toTitleCase = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
export const getStateCode = (state) => state?.stateCode || state?.StateCode || state?.abbreviation || state?.Abbreviation || state?.code || state?.Code || state?.utilityState || state?.UtilityState || state?.utilityStateName || state?.UtilityStateName || (String(state?.stateName || state?.StateName || "").length <= 3 ? state?.stateName || state?.StateName : "") || "";

export const getStateAbbreviation = (row, states = []) => {
  const abbreviation = (state) => state?.abbreviation || state?.Abbreviation || "";
  if (abbreviation(row)) return abbreviation(row);
  const stateId = row?.stateId ?? row?.StateId ?? row?.stateID ?? row?.StateID;
  const guid = row?.enerexStateId ?? row?.EnerexStateId;
  const equal = (left, right) => left != null && right != null && String(left).toLowerCase() === String(right).toLowerCase();
  const match = states.find((state) =>
    (stateId != null && equal(stateId, getStateId(state))) ||
    (guid && guid !== EMPTY_GUID && equal(guid, state.enerexIdentifier ?? state.EnerexIdentifier ?? state.enerexStateId ?? state.EnerexStateId)));
  if (match) return abbreviation(match);
  const name = row?.stateName || row?.StateName || row?.utilityStateName || row?.UtilityStateName || row?.utilityState || row?.UtilityState || row?.stateCode || row?.StateCode;
  return abbreviation(states.find((state) => name && [getStateCode(state), getStateDisplayName(state), state.name, state.Name].some((value) => equal(name, value))));
};

export const getStateName = (row, states = []) => {
  const stateId = getStateId(row);
  if (stateId !== undefined && stateId !== null && stateId !== "") {
    const match = states.find((state) => String(getStateId(state)) === String(stateId));
    const matchName = toTitleCase(getStateDisplayName(match));
    if (matchName) return matchName;
  }

  const rowName = getStateDisplayName(row);
  const rowCode = getStateCode(row) || rowName;
  if (rowCode) {
    const match = states.find((state) => {
      const candidates = [getStateCode(state), state?.stateName, state?.StateName, state?.displayName, state?.DisplayName, state?.name, state?.Name].filter(Boolean);
      return candidates.some((candidate) => String(candidate).toLowerCase() === String(rowCode).toLowerCase());
    });
    const matchName = toTitleCase(getStateDisplayName(match));
    if (matchName) return matchName;
  }

  return toTitleCase(rowName);
};

export const getAliasStateName = (row, states = []) => {
  const stateId = row?.stateId ?? row?.StateId;
  if (stateId !== undefined && stateId !== null && stateId !== "") {
    const match = states.find((state) => String(getStateId(state)) === String(stateId));
    const matchCode = getStateCode(match);
    if (matchCode) return matchCode;
    const matchName = toTitleCase(getStateDisplayName(match));
    if (matchName) return matchName;
  }

  const rowCode = getStateCode(row);
  if (rowCode) return rowCode;

  return row?.stateName || row?.StateName || row?.utilityState || row?.UtilityState || row?.utilityStateName || row?.UtilityStateName || "";
};

export const getAliasStateOptionValue = (row, states = []) => {
  const stateId = row?.stateId ?? row?.StateId ?? row?.stateID ?? row?.StateID;
  if (stateId !== undefined && stateId !== null && stateId !== "") return stateId;

  const stateName = (row?.stateName || row?.StateName || row?.utilityState || row?.UtilityState || row?.utilityStateName || row?.UtilityStateName || row?.name || row?.Name || "").toLowerCase();
  const stateCode = (row?.stateCode || row?.StateCode || row?.code || row?.Code || row?.abbreviation || row?.Abbreviation || row?.utilityState || row?.UtilityState || "").toLowerCase();

  const match = states.find((state) => {
    const candidates = [state?.stateName, state?.StateName, state?.name, state?.Name, state?.stateCode, state?.StateCode, state?.code, state?.Code, state?.abbreviation, state?.Abbreviation].filter(Boolean).map((candidate) => String(candidate).toLowerCase());
    return (stateName && candidates.includes(stateName)) || (stateCode && candidates.includes(stateCode));
  });

  return getStateId(match);
};

export const getFormatStateName = (row, states = []) => {
  const stateId = row?.stateId ?? row?.StateId;
  if (stateId !== undefined && stateId !== null && stateId !== "") {
    const match = states.find((state) => String(getStateId(state)) === String(stateId));
    const matchName = toTitleCase(getStateDisplayName(match));
    if (matchName) return matchName;
  }

  const rowCode = getStateCode(row) || row?.utilityStateName || row?.UtilityStateName || row?.utilityState || row?.UtilityState || row?.stateName || row?.StateName || "";
  if (rowCode) {
    const match = states.find((state) => {
      const candidates = [
        getStateCode(state),
        state?.stateName,
        state?.StateName,
        state?.displayName,
        state?.DisplayName,
        state?.name,
        state?.Name,
      ].filter(Boolean);
      return candidates.some((candidate) => String(candidate).toLowerCase() === String(rowCode).toLowerCase());
    });
    const matchName = toTitleCase(getStateDisplayName(match));
    if (matchName) return matchName;
  }

  return toTitleCase(getStateDisplayName(row) || row?.utilityStateName || row?.UtilityStateName || row?.utilityState || row?.UtilityState || row?.stateName || row?.StateName || "");
};

export const toOption = (item, labelKeys = ["name", "Name", "displayName", "DisplayName"]) => ({
  value: getIdentifier(item),
  label: labelKeys.map((key) => item?.[key]).find(Boolean) || getIdentifier(item),
  raw: item,
});

export const getUserName = (user) => user?.email || user?.name || user?.nickname || "DataHub";

export const buildUtilitySavePayload = (payload, userName, userGuid) => ({
  id: payload.Id || 0,
  enerexUtilityId: payload.EnerexUtilityId || payload.EnerexIdentifier || EMPTY_GUID,
  name: payload.Name,
  shortName: payload.Name,
  longName: payload.LongName || "",
  legalName: payload.Name,
  website: payload.Website || payload.WebSite || "",
  logo: payload.Logo || "",
  dba: payload.DBA || "",
  fka: "",
  dunsNumber: payload.DunsNumber || payload.Duns || "",
  logoUrl: "",
  parentUtilityId: payload.ParentUtilityId || 0,
  parentUtilityName: "",
  mergedWithUtilityId: payload.MergedWithUtilityId || 0,
  mergedWithUtilityName: "",
  PurchaseOfReceivables: Boolean(payload.PurchaseOfReceivables),
  parentEnerexId: payload.ParentEnerexId || payload.ParentEXID || EMPTY_GUID,
  enerexMergedWithId: payload.EnerexMergedWithId || payload.MergedWithEXID || EMPTY_GUID,
  enerexMarketId: null,
  isECLEnabled: false,
  isPricing: false,
  commodityId: null,
  aliases: [],
  utilityStates: [],
  utilityRateClasses: [],
  userName,
  stateId: 0,
});
